import {
  createContext,
  FC,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react'
import { useDebounce } from 'hooks/hooks'
import {
  TDashboardFilters,
  TFilterWithOptionsName,
  TUpdateOptionsFunctionParams
} from '../types'
import { TOption } from '../../../components/Select/types'
import {
  applyFiltersRequest,
  getContractCategoriesOptions,
  getFiltersRequest,
  getHospitalOptions,
  getHSOptions,
  getLedByOptions,
  getPortfolioOptions,
  getUserOptions,
  removeFiltersRequest,
  updateFiltersRequest
} from '../Filters/api'
import {
  formatDashboardFiltersResponse,
  formatDashboardFiltersToResponse
} from '../Filters/utils'
import { formatOptionIdValue } from '../../../helper/optionsFormatters'
import { Form } from 'antd'
import { FormInstance } from 'antd/lib/form/hooks/useForm'
import { useDashboardAbility } from './DashboardAbilityProvider'
import { ACTIONS, dashboardSubject, SUBJECTS } from '../Abilities'
import { useSelector } from 'react-redux'
import { getUser } from 'redux/store/user/getters'

type ContextProps = {
  state: {
    hideFilter: boolean
    form: FormInstance<TDashboardFilters>
    filters: TDashboardFilters
    filtersApplied: boolean
    filtersWithOptionsName: TFilterWithOptionsName[]
    hsOptions: TOption[]
    hospitalOptions: TOption[]
    ledByOptions: TOption[]
    userOptions: TOption[]
    portfolioOptions: TOption[]
    ccOptions: TOption[]
    canSeeHealthSystemsFilter: boolean
    canSeeHospitalsFilter: boolean
    canSeeLedByFilter: boolean
    canSeePortfolioFilter: boolean
    canSeeCcFilter: boolean
    canSeeUserFilter: boolean
    disableHospitalsSelect: boolean
    disableCategoriesSelect: boolean
    disableUserSelect: boolean
    canSeeHealthSystemsFilterMultiSelect: boolean
    canSeeHospitalsFilterMultiSelect: boolean
    canSeePortfolioFilterMultiSelect: boolean
    canSeeCcFilterMultiSelect: boolean
    canSeeLedByFilterMultiSelect: boolean
    canSeeUserFilterMultiSelect: boolean
    debouncedFilters: TDashboardFilters
    updateFilters: boolean
    canSeeExpectedSavingsMenu: boolean
    canSeeMyHealthSystemLabel: boolean
  }
  actions: {
    resetFilters: () => void
    applyFilters: (filters: TDashboardFilters) => void
    onHSChange: (hs: string[], params: TUpdateOptionsFunctionParams) => void
    onPortfolioChange: (
      portfolio: string[],
      params: TUpdateOptionsFunctionParams
    ) => void
    onFilterItemRemove: (key: keyof TDashboardFilters, uuid: string) => void
    onCancelUpdateForm: () => void
    setFiltersUpdated: () => void
  }
}

export const filtersInitialState = {
  health_systems: [],
  hospitals: [],
  led_by: [],
  user: [],
  contract_categories: [],
  portfolio: []
}

const DashboardFilterContext = createContext<ContextProps>({
  state: {} as ContextProps['state'],
  actions: {} as ContextProps['actions']
})

const DashboardFilterContextProvider: FC<PropsWithChildren> = ({
  children
}) => {
  const ability = useDashboardAbility()
  // hide filter for prod and staging environments as pending functionality
  const hideFilter = useMemo(
    () => ability.cannot(ACTIONS.VIEW, SUBJECTS.FILTERS),
    [ability]
  )

  const [form] = Form.useForm<TDashboardFilters>()

  const formHSValue = Form.useWatch('health_systems', form)
  const formPortfolioValue = Form.useWatch('portfolio', form)

  const [hsOptions, _setHsOptions] = useState<TOption[]>([])
  const [hospitalOptions, _setHospitalOptions] = useState<TOption[]>([])
  const [ledByOptions, _setLedByOptions] = useState<TOption[]>([])
  const [userOptions, _setUserOptions] = useState<TOption[]>([])
  const [portfolioOptions, _setPortfolioOptions] = useState<TOption[]>([])
  const [ccOptions, _setCcOptions] = useState<TOption[]>([])

  const [filters, setFilters] = useState<TDashboardFilters>(filtersInitialState)
  const debouncedFilters = useDebounce(filters, 500)
  const skipApplyFiltersRef = useRef(true)
  const filtersSavedOnServerRef = useRef(false)
  const [updateFilters, _setUpdateFilters] = useState<boolean>(true)

  const user = useSelector(getUser)

  const canSeeHealthSystemsFilter = useMemo(
    () =>
      ability.can(
        ACTIONS.VIEW,
        dashboardSubject(SUBJECTS.HEALTH_SYSTEMS_FILTER, user)
      ),
    [ability, user]
  )

  const canSeePredefinedHS = useMemo(
    () =>
      canSeeHealthSystemsFilter &&
      ability.can(ACTIONS.VIEW, SUBJECTS.HEALTH_SYSTEMS_PREDEFINED),
    [ability, canSeeHealthSystemsFilter]
  )
  const canSeeHospitalsFilter = useMemo(
    () => ability.can(ACTIONS.VIEW, SUBJECTS.HOSPITALS_FILTER),
    [ability]
  )
  const canSeeLedByFilter = useMemo(
    () => ability.can(ACTIONS.VIEW, SUBJECTS.LED_BY_FILTER),
    [ability]
  )
  const canSeePortfolioFilter = useMemo(
    () => ability.can(ACTIONS.VIEW, SUBJECTS.PORTFOLIO_FILTER),
    [ability]
  )
  const canSeeCcFilter = useMemo(
    () => ability.can(ACTIONS.VIEW, SUBJECTS.CC_FILTER),
    [ability]
  )
  const canSeeUserFilter = useMemo(
    () => ability.can(ACTIONS.VIEW, SUBJECTS.USER_FILTER),
    [ability]
  )

  const canSeeHealthSystemsFilterMultiSelect = useMemo(
    () =>
      ability.can(
        ACTIONS.VIEW,
        dashboardSubject(SUBJECTS.HEALTH_SYSTEMS_FILTER_MULTISELECT, user)
      ),
    [ability, user]
  )
  const canSeeHospitalsFilterMultiSelect = useMemo(
    () => ability.can(ACTIONS.VIEW, SUBJECTS.HOSPITALS_FILTER_MULTISELECT),
    [ability]
  )
  const canSeePortfolioFilterMultiSelect = useMemo(
    () => ability.can(ACTIONS.VIEW, SUBJECTS.PORTFOLIO_FILTER_MULTISELECT),
    [ability]
  )
  const canSeeCcFilterMultiSelect = useMemo(
    () => ability.can(ACTIONS.VIEW, SUBJECTS.CC_FILTER_MULTISELECT),
    [ability]
  )
  const canSeeLedByFilterMultiSelect = useMemo(
    () => ability.can(ACTIONS.VIEW, SUBJECTS.LED_BY_FILTER_MULTISELECT),
    [ability]
  )
  const canSeeUserFilterMultiSelect = useMemo(
    () => ability.can(ACTIONS.VIEW, SUBJECTS.USER_FILTER_MULTISELECT),
    [ability]
  )

  const canSeeExpectedSavingsMenu = ability.can(
    ACTIONS.VIEW,
    SUBJECTS.EXPECTED_SAVINGS_MENU
  )

  const canSeeMyHealthSystemLabel = useMemo(
    () => ability.can(ACTIONS.VIEW, SUBJECTS.MY_HEALTH_SYSTEM_LABEL),
    [ability]
  )

  const disableCategoriesSelect = useMemo(
    () => portfolioOptions.length > 0 && !formPortfolioValue?.length,
    [portfolioOptions.length, formPortfolioValue?.length]
  )

  const disableHospitalsSelect = useMemo(
    () => hsOptions.length > 0 && !formHSValue?.length,
    [formHSValue?.length, hsOptions.length]
  )

  const disableUserSelect = useMemo(
    () => hsOptions.length > 0 && !formHSValue?.length,
    [hsOptions.length, formHSValue?.length]
  )

  const filtersApplied = Object.keys(filters)
    .filter((f) => canSeeHealthSystemsFilter || f !== 'health_systems')
    .find((f) => filters[f]?.length > 0)

  const applyFilters = useCallback((filters: TDashboardFilters) => {
    setFilters(filters)
  }, [])

  const onHSChange = useCallback(
    (health_systems, { updateFilters }: TUpdateOptionsFunctionParams) => {
      const formHospitals = updateFilters
        ? filters.hospitals
        : form.getFieldValue('hospitals')

      const formUsers = updateFilters
        ? filters.user
        : form.getFieldValue('user')

      if (health_systems.length) {
        const _updatedHospitals: TDashboardFilters['hospitals'] = []
        const _updatedUsers: TDashboardFilters['user'] = []
        if (canSeeHospitalsFilter) {
          getHospitalOptions({ health_system: health_systems }).then(
            (hospitalOptionsResponse) => {
              if (hospitalOptionsResponse?.data?.results) {
                _setHospitalOptions(
                  hospitalOptionsResponse.data.results.map(formatOptionIdValue)
                )
                formHospitals.forEach((hospital) => {
                  if (
                    hospitalOptionsResponse.data.results.find(
                      (rHospital) => rHospital.id === hospital
                    )
                  ) {
                    _updatedHospitals.push(hospital)
                  }
                })
                form.setFieldsValue({
                  hospitals: _updatedHospitals
                })
              }
            }
          )
        }
        if (canSeeUserFilter) {
          getUserOptions({ health_system: health_systems }).then(
            (userOptionsResponse) => {
              if (userOptionsResponse?.data?.results) {
                _setUserOptions(
                  userOptionsResponse.data.results.map(formatOptionIdValue)
                )
                formUsers.forEach((userItem) => {
                  if (
                    userOptionsResponse.data.results.find(
                      (rUser) => rUser.id === userItem
                    )
                  ) {
                    _updatedUsers.push(userItem)
                  }
                })
                form.setFieldsValue({
                  user: _updatedUsers
                })
              }
            }
          )
        }
        if (updateFilters)
          setFilters({
            ...filters,
            health_systems: health_systems,
            user: _updatedUsers,
            hospitals: _updatedHospitals
          })
      } else {
        form.setFieldsValue({
          hospitals: [],
          user: []
        })
        if (updateFilters) {
          setFilters({
            ...filters,
            health_systems: [],
            user: [],
            hospitals: []
          })
        }
        _setHospitalOptions([])
        _setUserOptions([])
      }
    },
    [filters, form, canSeeHospitalsFilter, canSeeUserFilter]
  )

  const onPortfolioChange = useCallback(
    (portfolio, { updateFilters }: TUpdateOptionsFunctionParams) => {
      const formCC = updateFilters
        ? filters.contract_categories
        : form.getFieldValue('contract_categories')

      if (portfolio.length) {
        const _updatedCC: TDashboardFilters['contract_categories'] = []
        if (canSeeCcFilter) {
          getContractCategoriesOptions({ portfolio_id: portfolio }).then(
            (contractCategoriesOptionsResponse) => {
              if (contractCategoriesOptionsResponse?.data?.results) {
                _setCcOptions(
                  contractCategoriesOptionsResponse.data.results.map(
                    formatOptionIdValue
                  )
                )
                formCC.forEach((cc) => {
                  if (
                    contractCategoriesOptionsResponse.data.results.find(
                      (rCC) => rCC.id === cc
                    )
                  ) {
                    _updatedCC.push(cc)
                  }
                })
                form.setFieldsValue({
                  contract_categories: _updatedCC
                })
              }
            }
          )
        }
        if (updateFilters)
          setFilters({
            ...filters,
            portfolio,
            contract_categories: _updatedCC
          })
      } else {
        form.setFieldsValue({
          contract_categories: []
        })
        if (updateFilters) {
          setFilters({
            ...filters,
            portfolio: [],
            contract_categories: []
          })
        }
        _setCcOptions([])
      }
    },
    [filters, form, canSeeCcFilter]
  )

  const resetFilters = useCallback(() => {
    skipApplyFiltersRef.current = true
    filtersSavedOnServerRef.current = false
    applyFilters(filtersInitialState)
    removeFiltersRequest().then(() => {
      _setUpdateFilters(true)
    })
  }, [applyFilters])

  useEffect(() => {
    if (hideFilter) return
    if (skipApplyFiltersRef.current) {
      skipApplyFiltersRef.current = false
      return
    }
    const data = formatDashboardFiltersToResponse(debouncedFilters)

    // if the last item is removed, remove the filters
    if (
      filtersSavedOnServerRef.current &&
      !Object.keys(debouncedFilters).find(
        (f) => debouncedFilters[f]?.length > 0
      )
    ) {
      removeFiltersRequest().then(() => {
        filtersSavedOnServerRef.current = false
        _setUpdateFilters(true)
      })
    } else if (filtersSavedOnServerRef.current) {
      // if the filter is applied, update the filters
      updateFiltersRequest(data).then(() => {
        _setUpdateFilters(true)
      })
    } else {
      // if no filters are saved on server, apply the filters
      applyFiltersRequest(data)
        .then((response) => {
          if (response?.data) {
            filtersSavedOnServerRef.current = true
          }
        })
        .then(() => {
          _setUpdateFilters(true)
        })
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedFilters, hideFilter])

  const onFilterItemRemove = useCallback(
    (key: keyof TDashboardFilters, uuid: string) => {
      applyFilters({
        ...filters,
        [key]: filters[key]?.filter((i) => i !== uuid)
      })
      if (key === 'health_systems') {
        onHSChange(
          filters.health_systems.filter((i) => i !== uuid),
          {
            updateFilters: true
          }
        )
      } else if (key === 'portfolio') {
        onPortfolioChange(
          filters.portfolio.filter((i) => i !== uuid),
          {
            updateFilters: true
          }
        )
      }
    },
    [applyFilters, filters, onHSChange, onPortfolioChange]
  )

  const getInitPickersData = useCallback(() => {
    if (hideFilter) return

    const requests: Promise<void>[] = []

    if (canSeeHealthSystemsFilter) {
      requests.push(
        getHSOptions().then((hsOptionsResponse) => {
          if (hsOptionsResponse?.data?.results) {
            _setHsOptions(
              hsOptionsResponse.data.results.map(formatOptionIdValue)
            )
          }
        })
      )
    }

    if (canSeePortfolioFilter) {
      requests.push(
        getPortfolioOptions().then((portfolioOptionsResponse) => {
          if (portfolioOptionsResponse?.data?.results) {
            _setPortfolioOptions(
              portfolioOptionsResponse.data.results.map(formatOptionIdValue)
            )
          }
        })
      )
    }

    if (canSeeLedByFilter) {
      requests.push(
        getLedByOptions().then((ledByOptionsResponse) => {
          if (ledByOptionsResponse?.data?.results) {
            _setLedByOptions(
              ledByOptionsResponse.data.results.map(formatOptionIdValue)
            )
          }
        })
      )
    }

    void Promise.all(requests).then(() => {
      getFiltersRequest().then((filtersResponse) => {
        if (!filtersResponse?.data) return

        const hasSavedFilters = Object.keys(filtersResponse.data).find(
          (f) => filtersResponse.data[f]?.length > 0
        )

        if (hasSavedFilters) {
          skipApplyFiltersRef.current = true
          filtersSavedOnServerRef.current = true
          const formattedFilters = formatDashboardFiltersResponse(
            filtersResponse.data
          )
          if (formattedFilters.health_systems?.length) {
            onHSChange(formattedFilters.health_systems, {
              updateFilters: true
            })
          }
          if (formattedFilters.portfolio?.length) {
            onPortfolioChange(formattedFilters.portfolio, {
              updateFilters: true
            })
          }
          setFilters(formattedFilters)
        } else if (
          (canSeePredefinedHS || !canSeeHealthSystemsFilter) &&
          user.health_system
        ) {
          skipApplyFiltersRef.current = true
          const predefinedFilters: TDashboardFilters = {
            ...filtersInitialState,
            health_systems: [user.health_system]
          }

          applyFiltersRequest(
            formatDashboardFiltersToResponse(predefinedFilters)
          ).then((response) => {
            if (!response?.data) return

            filtersSavedOnServerRef.current = true
            const formattedFilters = formatDashboardFiltersResponse(
              response.data
            )
            setFilters(formattedFilters)
            onHSChange(formattedFilters.health_systems, {
              updateFilters: true
            })
            _setUpdateFilters(true)
          })
        }
      })
    })
  }, [
    hideFilter,
    canSeeHealthSystemsFilter,
    canSeePortfolioFilter,
    canSeeLedByFilter,
    canSeePredefinedHS,
    user.health_system,
    onHSChange,
    onPortfolioChange
  ])

  useEffect(() => {
    getInitPickersData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const filtersWithOptionsName = useMemo(() => {
    const _filtersWithOptionsName: TFilterWithOptionsName[] = []
    Object.keys(filters).forEach((key) => {
      filters[key]?.map((item) => {
        let options: TOption[]
        switch (key) {
          case 'health_systems':
            if (!canSeeHealthSystemsFilter) return
            options = hsOptions
            break
          case 'hospitals':
            options = hospitalOptions
            break
          case 'portfolio':
            options = portfolioOptions
            break
          case 'led_by':
            options = ledByOptions
            break
          case 'user':
            options = userOptions
            break
          default:
            options = ccOptions
            break
        }
        _filtersWithOptionsName.push({
          value: item,
          name: options.find((i) => i.value === item)?.label ?? '',
          key
        })
      })
    })
    return _filtersWithOptionsName
  }, [
    filters,
    hsOptions,
    hospitalOptions,
    ledByOptions,
    portfolioOptions,
    userOptions,
    ccOptions,
    canSeeHealthSystemsFilter
  ])

  const onCancelUpdateForm = useCallback(() => {
    form.setFieldsValue(filters)
    // reset options value to applied data
    if (filters.health_systems?.length)
      onHSChange(filters.health_systems, { updateFilters: true })
    if (filters.portfolio?.length)
      onPortfolioChange(filters.portfolio, { updateFilters: true })
  }, [filters, form, onHSChange, onPortfolioChange])

  const setFiltersUpdated = useCallback(() => {
    _setUpdateFilters(false)
  }, [])

  const context = useMemo(
    () => ({
      state: {
        hideFilter,
        form,
        filters,
        filtersApplied: Boolean(filtersApplied),
        filtersWithOptionsName,
        hsOptions,
        hospitalOptions,
        ledByOptions,
        userOptions,
        portfolioOptions,
        ccOptions,
        canSeeHealthSystemsFilter,
        canSeeHospitalsFilter,
        canSeeLedByFilter,
        canSeePortfolioFilter,
        canSeeCcFilter,
        canSeeUserFilter,
        disableCategoriesSelect,
        disableHospitalsSelect,
        disableUserSelect,
        canSeeHealthSystemsFilterMultiSelect,
        canSeeHospitalsFilterMultiSelect,
        canSeePortfolioFilterMultiSelect,
        canSeeCcFilterMultiSelect,
        canSeeLedByFilterMultiSelect,
        canSeeUserFilterMultiSelect,
        debouncedFilters,
        updateFilters,
        canSeeExpectedSavingsMenu,
        canSeeMyHealthSystemLabel
      },
      actions: {
        resetFilters,
        applyFilters,
        onHSChange,
        onPortfolioChange,
        onFilterItemRemove,
        onCancelUpdateForm,
        setFiltersUpdated
      }
    }),
    [
      filters,
      form,
      filtersApplied,
      resetFilters,
      applyFilters,
      hsOptions,
      hospitalOptions,
      ledByOptions,
      userOptions,
      portfolioOptions,
      ccOptions,
      onHSChange,
      onPortfolioChange,
      onFilterItemRemove,
      filtersWithOptionsName,
      onCancelUpdateForm,
      hideFilter,
      canSeeHealthSystemsFilter,
      canSeeHospitalsFilter,
      canSeeLedByFilter,
      canSeePortfolioFilter,
      canSeeCcFilter,
      canSeeUserFilter,
      disableCategoriesSelect,
      disableHospitalsSelect,
      canSeeHealthSystemsFilterMultiSelect,
      canSeeHospitalsFilterMultiSelect,
      canSeePortfolioFilterMultiSelect,
      canSeeCcFilterMultiSelect,
      canSeeLedByFilterMultiSelect,
      canSeeUserFilterMultiSelect,
      debouncedFilters,
      setFiltersUpdated,
      updateFilters,
      disableUserSelect,
      canSeeExpectedSavingsMenu,
      canSeeMyHealthSystemLabel
    ]
  )

  return (
    <DashboardFilterContext.Provider value={context}>
      {children}
    </DashboardFilterContext.Provider>
  )
}

export const useDashboardFilterContext = () =>
  useContext(DashboardFilterContext)

export default DashboardFilterContextProvider
